import * as flatbuffers from "flatbuffers";
import { fromByteBuffer } from "flatgeobuf/lib/mjs/header-meta.js";
import {
  HEADER_MAX_BUFFER_SIZE,
  magicbytes,
  SIZE_PREFIX_LEN,
} from "flatgeobuf/lib/mjs/constants.js";

// Byte layout of a FlatGeobuf file, mirroring the library's `HttpReader.open`:
//
//   [0, 8)                   magic bytes
//   [8, 12)                  uint32 LE header length
//   [12, 12 + headerLength)  header flatbuffer
//
// Since flatgeobuf 4 `fromByteBuffer` reads the header with
// `getSizePrefixedRootAsHeader`, so the bytes handed to it must start at the
// length prefix (byte 8), not at the header itself (byte 12). Passing the bare
// header does not throw: the root offset is read from the wrong place and every
// field comes back as its default, so `featuresCount` is 0 and a perfectly good
// file is reported as empty. `fgbHeaderRange` is the single place that knows
// where the header request has to start.

export const FGB_PREFIX_LEN = magicbytes.length + SIZE_PREFIX_LEN;

/**
 * Validates the magic bytes and returns the header length from the first
 * `FGB_PREFIX_LEN` bytes of a file.
 *
 * @param {Uint8Array} prefix
 * @returns {number}
 */
export function parseFgbHeaderLength(prefix) {
  if (prefix.byteLength < FGB_PREFIX_LEN) {
    throw new Error("Not a FlatGeobuf file");
  }
  if (!prefix.subarray(0, 3).every((b, i) => magicbytes[i] === b)) {
    throw new Error("Not a FlatGeobuf file");
  }
  const headerLength = new DataView(
    prefix.buffer,
    prefix.byteOffset + magicbytes.length,
    SIZE_PREFIX_LEN,
  ).getUint32(0, true);
  if (headerLength > HEADER_MAX_BUFFER_SIZE || headerLength < 8) {
    throw new Error("Invalid header size");
  }
  return headerLength;
}

/**
 * The byte range to request for a header `fromByteBuffer` can parse: the
 * size prefix plus the header.
 *
 * @param {number} headerLength
 * @returns {{start: number, length: number}}
 */
export function fgbHeaderRange(headerLength) {
  return { start: magicbytes.length, length: SIZE_PREFIX_LEN + headerLength };
}

/**
 * @param {Uint8Array} sizePrefixedHeader the bytes of `fgbHeaderRange`
 * @returns {number}
 */
export function parseFgbFeaturesCount(sizePrefixedHeader) {
  return fromByteBuffer(new flatbuffers.ByteBuffer(sizePrefixedHeader))
    .featuresCount;
}
